import { PaginationControls } from "@/components/data-table/PaginationControls";
import Alert from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { ApolloError } from "@apollo/client";
import {
  SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import { useState } from "react";
import { SubscriptionColumn } from "../../assets/SubscriptionColumns";
import { SubscriptionTable } from "../../types/subscriptions.type";
import { SubscriptionsTableContent } from "./SubscriptionsTableContent";
import { SubscriptionsTableSkeleton } from "./SubscriptionsTableSkeleton";

interface Props<TData> {
  data: TData[];
  loading: boolean;
  error?: ApolloError;
  columns: SubscriptionColumn[];
}

export function SubscriptionsTable<TData extends SubscriptionTable>({
  data,
  columns,
  loading,
  error
}: Props<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting }
  });

  if (error) {
    console.error(error);
    return (
      <Alert
        data-testid="subscriptions-error"
        title="Unable to load subscriptions"
        description="An error occurred while loading subscriptions. Please try to reload the page or report the issue."
      />
    );
  }

  return (
    <div>
      <div className="rounded border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              <SubscriptionsTableSkeleton />
            ) : (
              <SubscriptionsTableContent table={table} columns={columns} />
            )}
          </TableBody>
        </Table>
      </div>
      <div className="my-4">
        <PaginationControls table={table} />
      </div>
    </div>
  );
}
